import { HTTP_STATUS } from "components/constants";
import { CONFIGURATION_API, TRANSACTION } from "components/constants/api-paths";
import { extraStatementList, extraTransactionList } from "components/constants/preference.c";
import { DATE_FORMATS_OPTIONS } from "pages/user-settings/date-format";
import { FILE_FORMATS_VALUES } from "pages/user-settings/file-format";
import { setBusinessLocations, setDasmidOptions, setMasterEntityData, setMerchantData, setPaymentLoggroup, setReasonCodeList, setReleaseTourData, setSalesLeadFilterMerchant,setSalesLeadFilterPartner, setUserPreference } from "redux/features/gateway-config";
import { quickSort } from "./helper";
import { setIsV2Merchant, setSignedInAsMerchant } from "redux/features/auth";
import { jwtDecode } from "jwt-decode";
import { CURRENCY_FORMATS_OPTIONS } from "pages/user-settings/currency-type";
import { ACCESS_GROUP_NAMES } from "components/constants/access-group-name";
import { startLoader, stopLoader } from "redux/features/loader";
import { fetchTransactionFilterColumns } from "redux/features/transaction-table";


const guestGroups = [
  ACCESS_GROUP_NAMES.GUEST,
  ACCESS_GROUP_NAMES.GUESTSCHEDULER,
  ACCESS_GROUP_NAMES.GUESTTHIRDPARTY,
];

const baseUrl = `${process.env.REACT_APP_API_URL}`;


function transformChargebackReasonCode(input: any) {
  const output: any = {};

  for (const key in input) {
    // if (input?.hasOwnProperty(key)) {
    output[key] = input[key].map((item: any) => {
      const labelValue = `${item.ReasonCode} ${item.ReasonCodeDescription}`;
      return {
        label: labelValue,
        value: labelValue,
      };
    });
    // }
  }

  return output;
}

export const fetchWithAuth = async (endpoint: string, token: string) => {
  const response = await fetch(`${baseUrl}/${endpoint}`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      'x-api-key': process.env.REACT_APP_X_API_KEY || '',
      Authorization: token,
    },
  });

  if (!response.ok) {
    throw new Error(`Failed to fetch ${endpoint}: ${response.statusText}`);
  }

  return await response.json();
};

export function signedInAsMerchantFromIdToken(idToken: string): boolean {
  const decoded: any = jwtDecode(idToken);
  return (
    decoded['custom:MerchantIDLinked'] === true ||
    decoded['custom:MerchantIDLinked'] === 'true'
  );
}

export const getUserPreference = async (dispatch: any, idToken: string) => {
  const filterColumnApiPath = TRANSACTION.TRANSACTION_FILTER_COLUMNS;
  dispatch(setSignedInAsMerchant(signedInAsMerchantFromIdToken(idToken)));
  dispatch(fetchTransactionFilterColumns({ apiPath: filterColumnApiPath }));

  const response = await fetchWithAuth('dasconfig/user-preferences', idToken);
  const hasValidPreferences =
    response?.data?.[0]?.configuration?.formatType &&
    response?.data?.[0]?.configuration?.transactionList?.list?.default &&
    response?.data?.[0]?.configuration?.statementList?.list?.default;

  if (hasValidPreferences) {
    const { configuration } = response.data[0];
    const { transactionList, statementList } = configuration;

    dispatch(
      setUserPreference({
        ...configuration,
        transactionList: {
          ...transactionList,
          list: { ...transactionList.list, ...extraTransactionList },
        },
        statementList: {
          ...statementList,
          list: { ...statementList.list, ...extraStatementList },
        },
      }),
    );
  } else {
    dispatch(
      setUserPreference({
        transactionList: {
          selected: 'default',
          list: {
            default: {
              order: [],
              unChecked: [],
            },
            ...extraTransactionList,
          },
        },
        statementList: {
          selected: 'default',
          list: {
            default: {
              order: [],
              unChecked: [],
            },
            ...extraStatementList,
          },
        },
        dateFormatType: DATE_FORMATS_OPTIONS.DATE_TIME_AM_PM,
        formatType: FILE_FORMATS_VALUES.EXCEL,
        currencyType: CURRENCY_FORMATS_OPTIONS.USD,
      }),
    );
  }
};

export const getDasMidOptions = async (
  dispatch: any,
  groups: any,
  accessLevel: any,
  idToken: string,
) => {
  const logginInAsMerchant = groups?.includes('CUSTOMER');

  const response = await fetchWithAuth(CONFIGURATION_API, idToken);
  
  if ([HTTP_STATUS.OK]?.includes(response?.statusCode)) {
    const responseData = response?.data;
    const reasonCodeData = transformChargebackReasonCode(
      responseData?.chargebackReasonCode,
    );
    dispatch(setReasonCodeList(reasonCodeData));
    if (responseData?.masterEntityData) {
      dispatch(setMasterEntityData(responseData.masterEntityData));
    }
    if (responseData?.businessLocations) {
      dispatch(setBusinessLocations(responseData.businessLocations));
    }
    const merchantData = responseData?.merchantData;
    const dasmidOptions =
      merchantData 
      ? merchantData?.map(({ DASMID }: any) => DASMID)?.flat(1) 
      : [];

    const dasmidList: any =
      dasmidOptions && dasmidOptions?.length > 0
        ? quickSort([...new Set(dasmidOptions)])
        : [];

    dispatch(setDasmidOptions(dasmidList));
    dispatch(setReleaseTourData(responseData?.releaseTourData));

    const v2DasmidOptions =
      merchantData
        ?.map(({ V2DASMID }: any) => V2DASMID)
        ?.flat(1)
        ?.filter((item: any) => item) || [];

    const IsV2Merchant = merchantData?.find(
      (item: any) => item.IsV2Merchant
    );

    if (v2DasmidOptions?.length > 0 && IsV2Merchant) {
      if (logginInAsMerchant && accessLevel === 'ADMIN') {
        dispatch(setIsV2Merchant(true));
      } else if (!logginInAsMerchant) {
        dispatch(setIsV2Merchant(true));
      } else {
        dispatch(setIsV2Merchant(false));
      }
    }

    const v2dasmidList: any =
      v2DasmidOptions && v2DasmidOptions?.length > 0
        ? [...new Set(v2DasmidOptions)]
        : '';

    localStorage.setItem('v2DasmidOptions', v2dasmidList);

    const acquirerCode: any = responseData?.acquirers;
    localStorage.setItem('acquirerCode', acquirerCode);

    const acquirerMIDData: any =responseData?.acquirerMIDData? quickSort(
      Object.values(responseData?.acquirerMIDData)?.flat(1),
    ):[];
    localStorage.setItem('acquirerMIDData', acquirerMIDData);
    if (merchantData) dispatch(setMerchantData(merchantData));
    if (!logginInAsMerchant) {
      if (responseData?.configData?.payment_loggroup)
        dispatch(
          setPaymentLoggroup(responseData?.configData?.payment_loggroup),
        );
    
      const salesLeadData = responseData?.salesLeadData;
      if (salesLeadData?.Merchant)
        dispatch(
          setSalesLeadFilterMerchant(salesLeadData?.Merchant),
        );
      if (salesLeadData?.Partner)
        dispatch(
          setSalesLeadFilterPartner(salesLeadData?.Partner),
        );
    }

    return response;
  } else {
    localStorage.setItem('dasmidOptions', '');
    localStorage.setItem('v2DasmidOptions', '');
    return response;
  }
};

export const handleAfterSignIn = async (
  dispatch: any,
  signInData: { token: { idToken: string }; Groups: any; accessLevel: any },
) => {
  const idToken = signInData?.token?.idToken;
  const groups = signInData?.Groups;
  const accessLevel = signInData?.accessLevel;

  // 🔐 Token Validation
  // ⎯⎯⎯⎯⎯⎯⎯⎯⎯⎯⎯
  if (!idToken) {
    throw new Error('Authentication failed: No token received');
  }

  // 👥 Guest User Short-Circuit
  // ⎯⎯⎯⎯⎯⎯⎯⎯⎯⎯⎯⎯⎯⎯⎯⎯

  if (groups?.some((group: string) => guestGroups.includes(group))) {
    dispatch(setSignedInAsMerchant(signedInAsMerchantFromIdToken(idToken)));
    return signInData;
  }

  // ⏳ Loading State Management
  // ⎯⎯⎯⎯⎯⎯⎯⎯⎯⎯⎯⎯⎯⎯⎯⎯
  try {
    dispatch(startLoader());

    // 🎨 User Preferences Section
    // ⎯⎯⎯⎯⎯⎯⎯⎯⎯⎯⎯⎯⎯⎯⎯⎯

    await getUserPreference(dispatch, idToken);

    // ⚙️ Gateway Configuration Section
    // ⎯⎯⎯⎯⎯⎯⎯⎯⎯⎯⎯⎯⎯⎯⎯⎯⎯⎯⎯
    // if (!groups.includes('THIRDPARTY'))
      await getDasMidOptions(dispatch, groups, accessLevel, idToken);

    return signInData;
  } finally {
    // 🏁 Cleanup Loading State
    // ⎯⎯⎯⎯⎯⎯⎯⎯⎯⎯⎯⎯⎯⎯
    dispatch(stopLoader());
  }
};